import { SYSTEM_LIST_IDS } from "./myVendorListsConstants";
import { normalizeVendorListMember } from "./myVendorListsUtils";

function vendorKey(value) {
  return String(value || "").trim().toLowerCase();
}

function vendorToListMember(vendor = {}) {
  return normalizeVendorListMember({
    provider_id: vendor.id ?? vendor.providerId ?? vendor.provider_id,
    name: vendor.name,
    logo: vendor.logoUrl ?? vendor.logo,
    service_categories: vendor.serviceCategories || vendor.services || [],
  });
}

/** Placeholder used when a bookmarked id has no live provider name yet. */
export function isSyntheticVendorListName(name, providerId) {
  const trimmed = String(name || "").trim();
  if (!trimmed) return true;
  const idPrefix = vendorKey(providerId).replace(/-/g, "").slice(0, 8);
  if (idPrefix && trimmed.toLowerCase() === `vendor ${idPrefix}`) {
    return true;
  }
  return /^Vendor [0-9a-f]{8}$/i.test(trimmed);
}

function resolveMemberFromDirectory(providerId, directories = []) {
  const id = vendorKey(providerId);
  if (!id) return null;
  for (const directory of directories) {
    const vendor = directory?.get?.(id);
    if (!vendor) continue;
    const member = vendorToListMember(vendor);
    if (member.providerId && !isSyntheticVendorListName(member.name, id)) {
      return member;
    }
  }
  return null;
}

function buildSystemList({ id, name, systemKind, notes, members = [] }) {
  return {
    id,
    name,
    notes: notes || "",
    isSystemList: true,
    systemKind,
    vendorCount: members.length,
    members,
    vendorMetadata: members,
    createdAt: null,
    createdByName: null,
  };
}

/** Compose pinned system lists + user lists (user lists A→Z). */
export function composeVendorLists({
  userLists = [],
  myVendors = [],
  bookmarkedProviderIds = [],
  myVendorsById = new Map(),
  providerDirectoryById = new Map(),
} = {}) {
  const myVendorsMembers = myVendors.map(vendorToListMember);

  const bookmarkedMembers = bookmarkedProviderIds
    .map((providerId) => {
      const liveMember = resolveMemberFromDirectory(providerId, [
        providerDirectoryById,
        myVendorsById,
      ]);
      if (liveMember) return liveMember;
      // Keep the id so the row is clickable; never invent a stale/snapshot name.
      return normalizeVendorListMember({
        provider_id: providerId,
        name: "",
      });
    })
    .filter((member) => member.providerId);

  const systemLists = [
    buildSystemList({
      id: SYSTEM_LIST_IDS.MY_VENDORS,
      name: "My Vendors",
      systemKind: "my-vendors",
      notes:
        "System list. Includes every vendor you have an in-platform request with, plus anyone you manually add.",
      members: myVendorsMembers,
    }),
    buildSystemList({
      id: SYSTEM_LIST_IDS.BOOKMARKED,
      name: "Bookmarked",
      systemKind: "bookmarked",
      notes: "System list. Bookmark vendors from anywhere and they show up here.",
      members: bookmarkedMembers,
    }),
  ];

  const sortedUserLists = [...userLists].sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""), undefined, {
      sensitivity: "base",
    })
  );

  return [...systemLists, ...sortedUserLists];
}

/** Enrich list members with live data from My Vendors (services, logos). */
export function enrichListMembersFromMyVendors(list, myVendorsById = new Map()) {
  const members = list.members || list.vendorMetadata || [];
  if (!members.length) return list;

  const enriched = members.map((member) => {
    const live = myVendorsById.get(vendorKey(member.providerId));
    if (!live) return member;
    const liveName = String(live.name || "").trim();
    const storedName = String(member.name || "").trim();
    return {
      ...member,
      name:
        liveName ||
        (isSyntheticVendorListName(storedName, member.providerId) ? "" : storedName),
      logoUrl: live.logoUrl || member.logoUrl || null,
      serviceCategories:
        live.serviceCategories?.length > 0
          ? live.serviceCategories
          : member.serviceCategories || [],
    };
  });

  return {
    ...list,
    members: enriched,
    vendorMetadata: enriched,
    vendorCount: enriched.length,
  };
}

export function enrichAllListsFromMyVendors(
  lists = [],
  myVendors = [],
  extraVendors = []
) {
  const byId = new Map();
  myVendors.forEach((vendor) => {
    const id = vendorKey(vendor?.id);
    if (id) byId.set(id, vendor);
  });
  extraVendors.forEach((vendor) => {
    const id = vendorKey(vendor?.id ?? vendor?.providerId);
    if (id) byId.set(id, vendor);
  });
  return lists.map((list) => enrichListMembersFromMyVendors(list, byId));
}
